import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PortalNavbar from '../PortalNavbar/PortalNavbar';
import BottomTabBar from '../Portal/BottomTabBar';
import { usePortalRole } from '../../contexts/PortalRoleContext';
import { useIsMobile } from '../../hooks/useIsMobile';
import { layout } from '../../styles/theme';
import styled from 'styled-components';

const LayoutContainer = styled.div`
  min-height: 100vh;
  background: #F8F8F8;
`;

const ContentContainer = styled.div`
  padding-top: 56px; /* Account for fixed navbar */
  min-height: calc(100vh - 56px);
  /* 家长端手机显示底部标签栏时，给内容留出底栏 + 安全区高度，避免被遮挡 */
  ${props => props.$bottomBar && `
    padding-bottom: calc(${layout.bottomNavHeight}px + env(safe-area-inset-bottom, 0px));
  `}
`;

const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F8F8F8;
`;

const LoadingSpinner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid #e5e7eb;
    border-top-color: #F1AF59;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }

  p {
    color: #6b7280;
    font-size: 14px;
  }
`;

const PortalLayout = ({ children }) => {
  const auth = useAuth();
  const { currentRole } = usePortalRole();
  const isMobile = useIsMobile();
  // 仅家长 + 手机时显示底部标签栏
  const showBottomBar = isMobile && currentRole === 'parent';

  // Show loading while checking auth
  if (auth.isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner>
          <div className="spinner" />
          <p>Loading...</p>
        </LoadingSpinner>
      </LoadingContainer>
    );
  }

  // Redirect to login if not authenticated
  if (!auth.isAuthenticated) {
    return <Navigate to="/portal/login" replace />;
  }

  // Check if user has portal access (any portal_* group)
  const userGroups = auth.user?.profile?.['cognito:groups'] || [];
  const hasPortalAccess = userGroups.some(group => group.startsWith('portal_'));

  // Redirect to home if no portal permission
  if (!hasPortalAccess) {
    return <Navigate to="/home" replace />;
  }

  return (
    <LayoutContainer>
      <PortalNavbar />
      <ContentContainer $bottomBar={showBottomBar}>
        {children}
      </ContentContainer>
      {showBottomBar && <BottomTabBar />}
    </LayoutContainer>
  );
};

export default PortalLayout;
